import { useState, useMemo } from 'react';
import { useForm, router } from '@inertiajs/react';
import { motion, AnimatePresence } from 'framer-motion';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis } from 'recharts';
import { Toaster, toast } from 'sonner';
import { useHotkeys } from 'react-hotkeys-hook';

// Service type configurations
const SERVICE_TYPES = {
    hotel: { icon: 'bi-building', color: '#f97316', label: 'Hotel' },
    transfer: { icon: 'bi-car-front', color: '#22c55e', label: 'Transfer' },
    sightseeing: { icon: 'bi-camera', color: '#3b82f6', label: 'Sightseeing' },
    meal: { icon: 'bi-cup-hot', color: '#ec4899', label: 'Meal' },
    visa: { icon: 'bi-passport', color: '#8b5cf6', label: 'Visa' },
    flight: { icon: 'bi-airplane', color: '#6366f1', label: 'Flight' },
    insurance: { icon: 'bi-shield-check', color: '#14b8a6', label: 'Insurance' },
    cruise: { icon: 'bi-water', color: '#06b6d4', label: 'Cruise' },
    guide: { icon: 'bi-person-badge', color: '#f59e0b', label: 'Guide' },
    other: { icon: 'bi-box', color: '#64748b', label: 'Other' },
};

const STATUSES = {
    pending: { label: 'Pending', color: 'secondary', icon: 'bi-clock' },
    requested: { label: 'Requested', color: 'info', icon: 'bi-send' },
    confirmed: { label: 'Confirmed', color: 'success', icon: 'bi-check-circle' },
    on_hold: { label: 'On Hold', color: 'warning', icon: 'bi-pause-circle' },
    waitlist: { label: 'Waitlist', color: 'primary', icon: 'bi-hourglass-split' },
    cancelled: { label: 'Cancelled', color: 'danger', icon: 'bi-x-circle' },
    modified: { label: 'Modified', color: 'warning', icon: 'bi-pencil-square' },
};

const PAYMENT_STATUSES = {
    pending: { label: 'Pending', color: 'secondary' },
    partial: { label: 'Partial', color: 'warning' },
    paid: { label: 'Paid', color: 'success' },
    refund_pending: { label: 'Refund Pending', color: 'info' },
    refunded: { label: 'Refunded', color: 'primary' },
};

export default function SupplierTab({ query, supplierBookings = [], suppliers = [], currencies = [], quotations = [] }) {
    const [filter, setFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [showForm, setShowForm] = useState(false);
    const [editingBooking, setEditingBooking] = useState(null);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [selectedBookings, setSelectedBookings] = useState([]);
    const [showAnalytics, setShowAnalytics] = useState(false);

    // Keyboard shortcuts
    useHotkeys('ctrl+n, cmd+n', (e) => {
        e.preventDefault();
        setEditingBooking(null);
        setShowForm(true);
        toast.info('Adding new booking...');
    }, { enableOnFormTags: false });

    useHotkeys('escape', () => {
        setShowForm(false);
        setShowStatusModal(false);
        setEditingBooking(null);
    });

    // Filter bookings
    const filteredBookings = useMemo(() => {
        return supplierBookings.filter(booking => {
            if (filter !== 'all' && booking.status !== filter) return false;
            if (typeFilter !== 'all' && booking.service_type !== typeFilter) return false;
            return true;
        });
    }, [supplierBookings, filter, typeFilter]);

    // Stats
    const stats = useMemo(() => ({
        total: supplierBookings.length,
        pending: supplierBookings.filter(b => b.status === 'pending').length,
        requested: supplierBookings.filter(b => b.status === 'requested').length,
        confirmed: supplierBookings.filter(b => b.status === 'confirmed').length,
        cancelled: supplierBookings.filter(b => b.status === 'cancelled').length,
        needsAction: supplierBookings.filter(b => ['pending', 'requested', 'on_hold', 'waitlist'].includes(b.status)).length,
        totalCost: supplierBookings.reduce((sum, b) => sum + parseFloat(b.cost_amount || 0), 0),
        confirmedCost: supplierBookings.filter(b => b.status === 'confirmed').reduce((sum, b) => sum + parseFloat(b.cost_amount || 0), 0),
    }), [supplierBookings]);

    // Chart data - Cost by service type
    const costByTypeData = useMemo(() => {
        const byType = {};
        supplierBookings.forEach(b => {
            const type = b.service_type || 'other';
            if (!byType[type]) byType[type] = 0;
            byType[type] += parseFloat(b.cost_amount || 0);
        });
        return Object.entries(byType).map(([type, value]) => ({
            name: SERVICE_TYPES[type]?.label || type,
            value,
            color: SERVICE_TYPES[type]?.color || '#64748b',
        }));
    }, [supplierBookings]);

    // Chart data - Status distribution
    const statusData = useMemo(() => {
        return Object.entries(STATUSES).map(([status, config]) => ({
            name: config.label,
            value: supplierBookings.filter(b => b.status === status).length,
            color: status === 'confirmed' ? '#22c55e' : status === 'pending' ? '#94a3b8' : status === 'cancelled' ? '#ef4444' : '#6366f1',
        })).filter(d => d.value > 0);
    }, [supplierBookings]);

    // Supplier options for react-select
    const supplierOptions = useMemo(() => {
        return suppliers.map(s => ({
            value: s.id,
            label: s.name,
        }));
    }, [suppliers]);

    // Get unique service types in bookings
    const serviceTypesInUse = useMemo(() => {
        return [...new Set(supplierBookings.map(b => b.service_type))];
    }, [supplierBookings]);

    const handleEdit = (booking) => {
        setEditingBooking(booking);
        setShowForm(true);
    };

    const handleDelete = (booking) => {
        if (confirm('Delete this supplier booking?')) {
            router.delete(`/queries/${query.id}/supplier-bookings/${booking.id}`, {
                preserveScroll: true,
                onSuccess: () => toast.success('Booking deleted'),
            });
        }
    };

    const handleStatusClick = (booking) => {
        setSelectedBooking(booking);
        setShowStatusModal(true);
    };

    const handleStatusChange = (newStatus, confirmationNumber = '', supplierRemarks = '') => {
        router.patch(`/queries/${query.id}/supplier-bookings/${selectedBooking.id}/status`, {
            status: newStatus,
            confirmation_number: confirmationNumber,
            supplier_remarks: supplierRemarks,
        }, {
            preserveScroll: true,
            onSuccess: () => {
                setShowStatusModal(false);
                toast.success(`Status updated to ${STATUSES[newStatus].label}`);
            },
        });
    };

    const handleBulkStatus = (status) => {
        if (selectedBookings.length === 0) return;
        router.post(`/queries/${query.id}/supplier-bookings/bulk-status`, {
            booking_ids: selectedBookings,
            status,
        }, {
            preserveScroll: true,
            onSuccess: () => {
                setSelectedBookings([]);
                toast.success(`${selectedBookings.length} booking(s) updated`);
            },
        });
    };

    const handleCreateFromQuotation = (quotation) => {
        const itemIds = quotation.items?.map(i => i.id) || [];
        if (itemIds.length === 0) {
            toast.error('No items in this quotation');
            return;
        }

        router.post(`/queries/${query.id}/quotations/${quotation.id}/create-bookings`, {
            item_ids: itemIds,
        }, {
            preserveScroll: true,
            onSuccess: () => toast.success('Bookings created from quotation'),
        });
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const formatCurrency = (amount, symbol = '₹') => {
        if (!amount) return `${symbol}0`;
        return `${symbol}${parseFloat(amount).toLocaleString('en-IN')}`;
    };

    return (
        <div className="qd-panel supplier-tab">
            <Toaster position="top-right" richColors />

            <div className="qd-panel-header">
                <h3>Supplier Bookings</h3>
                <div className="d-flex gap-2">
                    <button
                        className={`btn btn-sm ${showAnalytics ? 'btn-info' : 'btn-outline-info'}`}
                        onClick={() => setShowAnalytics(!showAnalytics)}
                    >
                        <i className="bi bi-pie-chart me-1"></i>
                        Analytics
                    </button>
                    {quotations.filter(q => q.status === 'accepted').length > 0 && (
                        <div className="dropdown">
                            <button className="btn btn-outline-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown">
                                <i className="bi bi-magic me-1"></i>
                                From Quotation
                            </button>
                            <ul className="dropdown-menu">
                                {quotations.filter(q => q.status === 'accepted').map(q => (
                                    <li key={q.id}>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => handleCreateFromQuotation(q)}
                                        >
                                            {q.quotation_number} - {q.title}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <button
                        className="btn btn-primary btn-sm"
                        onClick={() => { setEditingBooking(null); setShowForm(true); }}
                    >
                        <i className="bi bi-plus-lg me-1"></i>
                        Add Booking
                    </button>
                </div>
            </div>

            {/* Keyboard shortcuts hint */}
            <div className="shortcuts-hint">
                <span><kbd>Ctrl</kbd>+<kbd>N</kbd> New Booking</span>
                <span><kbd>Esc</kbd> Close Modal</span>
            </div>

            {/* Analytics Charts */}
            <AnimatePresence>
                {showAnalytics && supplierBookings.length > 0 && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="analytics-section"
                    >
                        <div className="row g-3">
                            <div className="col-md-4">
                                <div className="analytics-card">
                                    <h6>Cost by Service Type</h6>
                                    <ResponsiveContainer width="100%" height={180}>
                                        <PieChart>
                                            <Pie
                                                data={costByTypeData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={35}
                                                outerRadius={65}
                                                paddingAngle={2}
                                                dataKey="value"
                                            >
                                                {costByTypeData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value) => formatCurrency(value)} />
                                            <Legend wrapperStyle={{ fontSize: '11px' }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="analytics-card">
                                    <h6>Status Distribution</h6>
                                    <ResponsiveContainer width="100%" height={180}>
                                        <BarChart data={statusData} layout="vertical">
                                            <XAxis type="number" tick={{ fontSize: 10 }} />
                                            <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={70} />
                                            <Tooltip />
                                            <Bar dataKey="value" name="Count">
                                                {statusData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="analytics-card summary-card">
                                    <h6>Summary</h6>
                                    <div className="summary-stats">
                                        <div className="summary-item">
                                            <span className="summary-value">{formatCurrency(stats.totalCost)}</span>
                                            <span className="summary-label">Total Cost</span>
                                        </div>
                                        <div className="summary-item">
                                            <span className="summary-value text-success">{formatCurrency(stats.confirmedCost)}</span>
                                            <span className="summary-label">Confirmed</span>
                                        </div>
                                        <div className="summary-item">
                                            <span className="summary-value text-warning">{stats.needsAction}</span>
                                            <span className="summary-label">Needs Action</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Stats Row */}
            <div className="booking-stats">
                <div className="booking-stat" onClick={() => setFilter('all')}>
                    <span className="booking-stat-value">{stats.total}</span>
                    <span className="booking-stat-label">Total</span>
                </div>
                <div className="booking-stat" onClick={() => setFilter('pending')}>
                    <span className="booking-stat-value text-secondary">{stats.pending}</span>
                    <span className="booking-stat-label">Pending</span>
                </div>
                <div className="booking-stat" onClick={() => setFilter('requested')}>
                    <span className="booking-stat-value text-info">{stats.requested}</span>
                    <span className="booking-stat-label">Requested</span>
                </div>
                <div className="booking-stat" onClick={() => setFilter('confirmed')}>
                    <span className="booking-stat-value text-success">{stats.confirmed}</span>
                    <span className="booking-stat-label">Confirmed</span>
                </div>
                <div className="booking-stat" onClick={() => setFilter('cancelled')}>
                    <span className="booking-stat-value text-danger">{stats.cancelled}</span>
                    <span className="booking-stat-label">Cancelled</span>
                </div>
            </div>

            {/* Filters */}
            <div className="booking-filters">
                <div className="filter-group">
                    <span className="filter-label">Status:</span>
                    <div className="filter-buttons">
                        <button
                            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                            onClick={() => setFilter('all')}
                        >
                            All
                        </button>
                        {Object.entries(STATUSES).map(([key, config]) => (
                            <button
                                key={key}
                                className={`filter-btn ${filter === key ? 'active' : ''}`}
                                onClick={() => setFilter(key)}
                            >
                                <i className={`bi ${config.icon} me-1`}></i>
                                {config.label}
                            </button>
                        ))}
                    </div>
                </div>

                {serviceTypesInUse.length > 1 && (
                    <div className="filter-group">
                        <span className="filter-label">Service:</span>
                        <div className="filter-buttons">
                            <button
                                className={`filter-btn ${typeFilter === 'all' ? 'active' : ''}`}
                                onClick={() => setTypeFilter('all')}
                            >
                                All
                            </button>
                            {serviceTypesInUse.map(type => (
                                <button
                                    key={type}
                                    className={`filter-btn ${typeFilter === type ? 'active' : ''}`}
                                    onClick={() => setTypeFilter(type)}
                                    style={{ '--filter-color': SERVICE_TYPES[type]?.color }}
                                >
                                    <i className={`bi ${SERVICE_TYPES[type]?.icon}`} style={{ color: SERVICE_TYPES[type]?.color }}></i>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Bulk Actions */}
            {selectedBookings.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="bulk-actions"
                >
                    <span className="fw-medium">{selectedBookings.length} selected</span>
                    <button className="btn btn-sm btn-outline-info" onClick={() => handleBulkStatus('requested')}>
                        <i className="bi bi-send me-1"></i> Mark Requested
                    </button>
                    <button className="btn btn-sm btn-outline-success" onClick={() => handleBulkStatus('confirmed')}>
                        <i className="bi bi-check-circle me-1"></i> Mark Confirmed
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleBulkStatus('cancelled')}>
                        <i className="bi bi-x-circle me-1"></i> Cancel
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedBookings([])}>
                        Clear
                    </button>
                </motion.div>
            )}

            {/* Bookings List */}
            <div className="bookings-list">
                {filteredBookings.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">
                            <i className="bi bi-inbox"></i>
                        </div>
                        <h5>No supplier bookings</h5>
                        <p>
                            {filter === 'all' && typeFilter === 'all'
                                ? 'Add supplier bookings to track confirmations and payments.'
                                : 'No bookings match the current filters.'}
                        </p>
                        <button
                            className="btn btn-primary"
                            onClick={() => { setEditingBooking(null); setShowForm(true); }}
                        >
                            <i className="bi bi-plus-lg me-2"></i>
                            Add First Booking
                        </button>
                    </div>
                ) : (
                    <AnimatePresence mode="popLayout">
                        {filteredBookings.map(booking => {
                            const serviceConfig = SERVICE_TYPES[booking.service_type] || SERVICE_TYPES.other;
                            const statusConfig = STATUSES[booking.status] || STATUSES.pending;
                            const paymentConfig = PAYMENT_STATUSES[booking.payment_status] || PAYMENT_STATUSES.pending;

                            const isApproachingCutoff = booking.cutoff_date &&
                                new Date(booking.cutoff_date) <= new Date(Date.now() + 3 * 24 * 60 * 60 * 1000) &&
                                booking.status !== 'confirmed' && booking.status !== 'cancelled';

                            return (
                                <motion.div
                                    key={booking.id}
                                    layout
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    className={`booking-card ${isApproachingCutoff ? 'urgent' : ''}`}
                                >
                                    <div className="booking-checkbox">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={selectedBookings.includes(booking.id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedBookings([...selectedBookings, booking.id]);
                                                } else {
                                                    setSelectedBookings(selectedBookings.filter(id => id !== booking.id));
                                                }
                                            }}
                                        />
                                    </div>

                                    <div className="booking-icon" style={{ backgroundColor: serviceConfig.color }}>
                                        <i className={`bi ${serviceConfig.icon}`}></i>
                                    </div>

                                    <div className="booking-content">
                                        <div className="booking-header">
                                            <h6 className="booking-name">{booking.service_name}</h6>
                                            <div className="booking-badges">
                                                <button
                                                    className={`badge bg-${statusConfig.color} border-0`}
                                                    onClick={() => handleStatusClick(booking)}
                                                    title="Click to change status"
                                                >
                                                    <i className={`bi ${statusConfig.icon} me-1`}></i>
                                                    {statusConfig.label}
                                                </button>
                                                {booking.confirmation_number && (
                                                    <span className="badge bg-success-subtle text-success">
                                                        #{booking.confirmation_number}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="booking-meta">
                                            {booking.supplier?.name && (
                                                <span className="booking-meta-item">
                                                    <i className="bi bi-shop"></i>
                                                    {booking.supplier.name}
                                                </span>
                                            )}
                                            <span className="booking-meta-item">
                                                <i className="bi bi-calendar3"></i>
                                                {formatDate(booking.service_date)}
                                                {booking.nights && ` (${booking.nights}N)`}
                                            </span>
                                            {booking.pax && (
                                                <span className="booking-meta-item">
                                                    <i className="bi bi-people"></i>
                                                    {booking.pax} pax
                                                </span>
                                            )}
                                        </div>

                                        {isApproachingCutoff && (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="booking-alert"
                                            >
                                                <i className="bi bi-exclamation-triangle"></i>
                                                Cutoff: {formatDate(booking.cutoff_date)}
                                            </motion.div>
                                        )}

                                        <div className="booking-footer">
                                            <div className="booking-cost">
                                                <span className="cost-label">Cost:</span>
                                                <span className="cost-value">{formatCurrency(booking.cost_amount)}</span>
                                            </div>
                                            <div className="booking-payment">
                                                <span className={`badge bg-${paymentConfig.color}-subtle text-${paymentConfig.color}`}>
                                                    Payment: {paymentConfig.label}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="booking-actions">
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => handleStatusClick(booking)}
                                            title="Update Status"
                                        >
                                            <i className="bi bi-arrow-repeat"></i>
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => handleEdit(booking)}
                                            title="Edit"
                                        >
                                            <i className="bi bi-pencil"></i>
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => handleDelete(booking)}
                                            title="Delete"
                                        >
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                )}
            </div>

            {/* Status Change Modal */}
            <AnimatePresence>
                {showStatusModal && selectedBooking && (
                    <StatusModal
                        booking={selectedBooking}
                        onClose={() => setShowStatusModal(false)}
                        onSave={handleStatusChange}
                    />
                )}
            </AnimatePresence>

            {/* Booking Form Modal */}
            <AnimatePresence>
                {showForm && (
                    <BookingFormModal
                        query={query}
                        booking={editingBooking}
                        suppliers={suppliers}
                        supplierOptions={supplierOptions}
                        currencies={currencies}
                        onClose={() => {
                            setShowForm(false);
                            setEditingBooking(null);
                        }}
                    />
                )}
            </AnimatePresence>

        </div>
    );
}

// Status Change Modal
function StatusModal({ booking, onClose, onSave }) {
    const [status, setStatus] = useState(booking.status);
    const [confirmationNumber, setConfirmationNumber] = useState(booking.confirmation_number || '');
    const [supplierRemarks, setSupplierRemarks] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(status, confirmationNumber, supplierRemarks);
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-backdrop"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                className="status-modal"
                onClick={e => e.stopPropagation()}
            >
                <div className="modal-header">
                    <h5>Update Status</h5>
                    <button className="btn-close" onClick={onClose}></button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        <div className="mb-3">
                            <label className="form-label">Status</label>
                            <div className="status-grid">
                                {Object.entries(STATUSES).map(([key, config]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        className={`status-option ${status === key ? 'active' : ''}`}
                                        onClick={() => setStatus(key)}
                                    >
                                        <i className={`bi ${config.icon}`} style={{ color: status === key ? '#fff' : undefined }}></i>
                                        <span>{config.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {status === 'confirmed' && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                className="mb-3"
                            >
                                <label className="form-label">Confirmation Number</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={confirmationNumber}
                                    onChange={e => setConfirmationNumber(e.target.value)}
                                    placeholder="Enter confirmation number"
                                    autoFocus
                                />
                            </motion.div>
                        )}

                        <div className="mb-3">
                            <label className="form-label">Supplier Remarks</label>
                            <textarea
                                className="form-control"
                                rows="3"
                                value={supplierRemarks}
                                onChange={e => setSupplierRemarks(e.target.value)}
                                placeholder="Any notes from the supplier..."
                            />
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary">
                            <i className="bi bi-check-lg me-1"></i>
                            Update Status
                        </button>
                    </div>
                </form>
            </motion.div>

        </motion.div>
    );
}

// Booking Form Modal with react-select and react-datepicker
function BookingFormModal({ query, booking, suppliers, supplierOptions, currencies, onClose }) {
    const isEditing = !!booking?.id;

    const { data, setData, post, put, processing, errors } = useForm({
        supplier_id: booking?.supplier_id || '',
        service_type: booking?.service_type || 'hotel',
        service_name: booking?.service_name || '',
        service_details: booking?.service_details || '',
        service_date: booking?.service_date ? new Date(booking.service_date) : null,
        service_end_date: booking?.service_end_date ? new Date(booking.service_end_date) : null,
        nights: booking?.nights || '',
        pax: booking?.pax || '',
        status: booking?.status || 'pending',
        confirmation_number: booking?.confirmation_number || '',
        supplier_reference: booking?.supplier_reference || '',
        cutoff_date: booking?.cutoff_date ? new Date(booking.cutoff_date) : null,
        cost_amount: booking?.cost_amount || '',
        currency_id: booking?.currency_id || '',
        payment_due_date: booking?.payment_due_date ? new Date(booking.payment_due_date) : null,
        remarks: booking?.remarks || '',
        internal_notes: booking?.internal_notes || '',
    });

    const handleSubmit = (e) => {
        e.preventDefault();

        const formData = {
            ...data,
            service_date: data.service_date?.toISOString().split('T')[0],
            service_end_date: data.service_end_date?.toISOString().split('T')[0],
            cutoff_date: data.cutoff_date?.toISOString().split('T')[0],
            payment_due_date: data.payment_due_date?.toISOString().split('T')[0],
        };

        if (isEditing) {
            put(`/queries/${query.id}/supplier-bookings/${booking.id}`, {
                data: formData,
                preserveScroll: true,
                onSuccess: () => {
                    toast.success('Booking updated');
                    onClose();
                },
            });
        } else {
            post(`/queries/${query.id}/supplier-bookings`, {
                data: formData,
                preserveScroll: true,
                onSuccess: () => {
                    toast.success('Booking created');
                    onClose();
                },
            });
        }
    };

    const serviceConfig = SERVICE_TYPES[data.service_type] || SERVICE_TYPES.other;
    const selectedSupplier = supplierOptions.find(s => s.value === data.supplier_id);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-backdrop"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                className="booking-form-modal"
                onClick={e => e.stopPropagation()}
            >
                <div className="modal-header">
                    <div className="d-flex align-items-center gap-2">
                        <div className="form-icon" style={{ backgroundColor: serviceConfig.color }}>
                            <i className={`bi ${serviceConfig.icon}`}></i>
                        </div>
                        <h5>{isEditing ? 'Edit Booking' : 'Add Supplier Booking'}</h5>
                    </div>
                    <button className="btn-close" onClick={onClose}></button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        {/* Service Type Tabs */}
                        <div className="service-type-tabs mb-4">
                            {Object.entries(SERVICE_TYPES).map(([type, config]) => (
                                <button
                                    key={type}
                                    type="button"
                                    className={`service-type-tab ${data.service_type === type ? 'active' : ''}`}
                                    style={{ '--tab-color': config.color }}
                                    onClick={() => setData('service_type', type)}
                                    title={config.label}
                                >
                                    <i className={`bi ${config.icon}`}></i>
                                </button>
                            ))}
                        </div>

                        <div className="row g-3">
                            <div className="col-12">
                                <label className="form-label">Service Name <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.service_name ? 'is-invalid' : ''}`}
                                    value={data.service_name}
                                    onChange={e => setData('service_name', e.target.value)}
                                    placeholder="e.g., JW Marriott Downtown"
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Supplier</label>
                                <Select
                                    options={supplierOptions}
                                    value={selectedSupplier}
                                    onChange={option => setData('supplier_id', option?.value || '')}
                                    isClearable
                                    placeholder="Select supplier..."
                                    classNamePrefix="react-select"
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Status</label>
                                <select
                                    className="form-select"
                                    value={data.status}
                                    onChange={e => setData('status', e.target.value)}
                                >
                                    {Object.entries(STATUSES).map(([key, config]) => (
                                        <option key={key} value={key}>{config.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">Service Date</label>
                                <DatePicker
                                    selected={data.service_date}
                                    onChange={date => setData('service_date', date)}
                                    className="form-control"
                                    dateFormat="dd MMM yyyy"
                                    placeholderText="Select date"
                                />
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">End Date</label>
                                <DatePicker
                                    selected={data.service_end_date}
                                    onChange={date => setData('service_end_date', date)}
                                    className="form-control"
                                    dateFormat="dd MMM yyyy"
                                    minDate={data.service_date}
                                    placeholderText="Select date"
                                />
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">Nights</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={data.nights}
                                    onChange={e => setData('nights', e.target.value)}
                                />
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">Pax</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={data.pax}
                                    onChange={e => setData('pax', e.target.value)}
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Confirmation #</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={data.confirmation_number}
                                    onChange={e => setData('confirmation_number', e.target.value)}
                                    placeholder="Supplier confirmation number"
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Cutoff Date</label>
                                <DatePicker
                                    selected={data.cutoff_date}
                                    onChange={date => setData('cutoff_date', date)}
                                    className="form-control"
                                    dateFormat="dd MMM yyyy"
                                    placeholderText="Select cutoff date"
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Cost Amount</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="form-control"
                                        value={data.cost_amount}
                                        onChange={e => setData('cost_amount', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Payment Due Date</label>
                                <DatePicker
                                    selected={data.payment_due_date}
                                    onChange={date => setData('payment_due_date', date)}
                                    className="form-control"
                                    dateFormat="dd MMM yyyy"
                                    placeholderText="Select due date"
                                />
                            </div>

                            <div className="col-12">
                                <label className="form-label">Service Details</label>
                                <textarea
                                    className="form-control"
                                    rows="2"
                                    value={data.service_details}
                                    onChange={e => setData('service_details', e.target.value)}
                                    placeholder="Additional details..."
                                />
                            </div>

                            <div className="col-12">
                                <label className="form-label">Internal Notes</label>
                                <textarea
                                    className="form-control"
                                    rows="2"
                                    value={data.internal_notes}
                                    onChange={e => setData('internal_notes', e.target.value)}
                                    placeholder="Internal notes..."
                                />
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={processing}>
                            {processing ? (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            ) : (
                                <i className={`bi ${isEditing ? 'bi-check-lg' : 'bi-plus-lg'} me-1`}></i>
                            )}
                            {isEditing ? 'Update' : 'Add'} Booking
                        </button>
                    </div>
                </form>
            </motion.div>

        </motion.div>
    );
}
